import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, MapPin, ArrowRight, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { api, formatApiError, formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";

/**
 * SEO landing page for /land-for-sale-in-:city and /plots-for-sale-in-:city
 * Arch §28.1.
 */
export default function CityListings({ defaultCategory = "LAND" }) {
  const { city } = useParams();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  // Convert URL slug back to display name (best-effort)
  const displayCity = (city || "").split("-").map((w) => w[0]?.toUpperCase() + w.slice(1)).join(" ");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [s, m] = await Promise.all([
          api.get("/v1/search/properties", { params: { city: displayCity, category: defaultCategory, limit: 30 } }),
          api.get("/web/seo/city", { params: { city: displayCity, category: defaultCategory } }),
        ]);
        if (!cancelled) {
          setItems(s.data?.items || s.data || []);
          setMeta(m.data);
        }
      } catch (requestError) {
        if (!cancelled) {
          setItems([]);
          setError(formatApiError(requestError));
        }
      }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [displayCity, defaultCategory, reloadKey]);

  return (
    <div className="container mx-auto px-4 py-10 max-w-6xl" data-testid="city-listing-page">
      <SEO meta={meta} />

      <nav className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3" aria-label="breadcrumb">
        <Link to="/" className="hover:text-primary">Home</Link>
        <span className="mx-2">/</span>
        <Link to={defaultCategory === "LAND" ? "/properties" : "/plots"} className="hover:text-primary">
          {defaultCategory === "LAND" ? "Land" : "Plots"}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{displayCity}</span>
      </nav>

      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter uppercase text-primary mb-3"
        data-testid="city-listing-h1">
        {defaultCategory === "LAND" ? "Land" : "Plots"} for sale in {displayCity}
      </h1>
      <p className="text-muted-foreground max-w-2xl mb-8">
        Browse published {defaultCategory === "LAND" ? "land parcels" : "plots"} for sale in {displayCity}, with available pricing, location, registry, mutation, and verification details shown on each listing.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin"/> Loading listings in {displayCity}…</div>
      ) : error ? (
        <Card className="border-destructive/30 bg-destructive/5 p-8 text-center" role="alert">
          <p className="font-bold">Listings could not be loaded</p>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <Button type="button" className="mt-4" onClick={() => setReloadKey((key) => key + 1)}><RefreshCw className="w-4 h-4 mr-2" />Try again</Button>
        </Card>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground" data-testid="empty-city-list">
          No listings yet in {displayCity}. <Link to="/properties" className="text-primary font-bold ml-1">Browse all</Link>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="city-listings-grid">
          {items.map((p) => (
            <Link key={p.property_id} to={`/properties/${p.property_id}`}
              className="group block rounded-md overflow-hidden border border-border hover:border-primary transition-colors"
              data-testid={`city-prop-${p.property_id}`}>
              <div className="aspect-[4/3] bg-secondary overflow-hidden">
                {p.images?.[0] && (
                  <img src={p.images[0]} alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    loading="lazy" decoding="async" />
                )}
              </div>
              <div className="p-4 space-y-1.5">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {p.verified_owner && <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">Verified Owner</Badge>}
                  {p.land_details?.registry_available && <Badge variant="outline">Registry</Badge>}
                </div>
                <h3 className="font-bold leading-tight line-clamp-2">{p.title}</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3"/>{p.location?.locality}, {p.location?.city}
                </p>
                <div className="flex items-center justify-between pt-1">
                  <span className="font-display font-bold text-lg">{formatPrice(p.price)}</span>
                  {p.land_details?.area && (
                    <span className="text-xs text-muted-foreground font-semibold uppercase">
                      {p.land_details.area} {p.land_details.area_unit?.replace("_", " ")}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-12 p-6 bg-secondary/30 border border-border rounded-md">
        <h2 className="font-display text-2xl font-bold mb-3">Looking for something specific in {displayCity}?</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Filter by area, facing, road width, corner plot, and registry status—or place a request through Plots on Demand for participating agents to review.
        </p>
        <Link to="/plot-on-demand" className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline">
          Open Plots on Demand <ArrowRight className="w-4 h-4"/>
        </Link>
      </div>
    </div>
  );
}
